// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { removeTab } from 'wdna-components/lib/tabs';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import Loader from 'wdna-components/lib/loaders/local/Loader';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { messageError, messageInfo } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { typeModule, TypeModuleDO } from '../../../requests';

import Form from '../OnlyNameForm';
import ButtonsForm from '../ButtonsForm';

interface Props {
  rowId?: number;
  notify?: () => void;
}

interface State {
  loading: boolean;
  data: Partial<TypeModuleDO>;
}

export default class Edit extends React.Component<Props, State> {
  private pid: string;
  private defaultValues: any;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.defaultValues = {
      name: '',
    };

    this.state = {
      loading: false,
      data: {},
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load();
  };

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = (): void => {
    const { rowId } = this.props || {};
    if (rowId && !isNaN(rowId)) {
      this.loadingStart();
      typeModule<TypeModuleDO>('get', String(rowId))
        ?.then((resp: TypeModuleDO) => {
          this.setState({
            data: {
              ...resp,
            }
          }, () => {
            this.defaultValues = _.cloneDeep(resp);
          });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    }
  };

  public loadingStart = (): void => {
    this.setState({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setState({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  public onReset = (): void => {
    this.setState({
      data: {
        ...this.defaultValues,
      }
    });
  };

  public submit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const { data } = this.state;

    this.loadingStart();
    typeModule<TypeModuleDO>('set', undefined, data)
      ?.then(() => {
        messageInfo('Success', `Module type successfully ${data.id ? 'edited' : 'created'}`, 5000);
        const { notify } = this.props || {};
        if (typeof notify === 'function') {
          notify();
        }
        window.store.dispatch(removeTab('MTE'));
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value, name } = e.target;
    this.setState((prevState: State) => ({
      data: {
        ...prevState.data,
        [name]: value,
      },
    }));
  };

  public render(): JSX.Element {
    const { loading, data } = this.state;

    return (
      <div id="content" className="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
                fullscreenbutton={false}
              >
                <div className="widget-body">
                  <Loader show={loading} overlay={false} />
                  <UiValidate>
                    <form className="smart-form" onSubmit={this.submit} method="post" encType="multipart/form-data">
                      <header>
                        <h2>
                          <span className="widget-icon" style={{ padding: '8px' }}>
                            <i className="fa fa-edit" />
                          </span>
                          Module-Type {data.id ? `Edit: ${data.id}` : 'New'}
                        </h2>
                      </header>
                      <Form formValues={this.state} onChange={this.handleChange} />
                      <ButtonsForm loading={loading} onReset={this.onReset} />
                    </form>
                  </UiValidate>
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
